<template>
  <div>
    <el-row v-loading="loading" class="host-insights-graph">
      <el-col v-if="hasChartData" :span="6" class="topten-entities">
        <el-tag
          size="mini"
          type="info"
          effect="plain"
          class="cursor-pointer"
          v-for="topTenSelect in topTenOptions"
          :key="topTenSelect.value"
          :class="[selectedTopTenOption === topTenSelect.value ? 'active' : '']"
          @click="changeTopTenSelection(topTenSelect, $event)"
          >{{ topTenSelect.label }}</el-tag
        >
        <top-ten-card
          v-loading="statsLoading"
          :col-headers="colHeaders"
          :entries="topStats"
          :starting-page-num="startPageNo"
          :page-size="pageSize"
          @top-ten-click="emitTopTenSelection"
          ref="toptenCard"
        />
      </el-col>
      <el-col :span="hasChartData ? 18 : 24" style="height: 320px;">
        <bar-chart-js
          v-if="chartLoading"
          :chart-id="chartId"
          :chart-data="hrlyStats.chartData"
          :chart-options="hrlyStats.chartOptions"
        ></bar-chart-js>
      </el-col>
    </el-row>
    <el-row class="c-stats_row_table" v-if="enableTableView">
      <el-col :span="24" class="c-stats_row_timeseries page-table" v-loading="tableLoading">
        <tree-table-element
          v-if="!tableLoading"
          :columns="statsTableColumns"
          :table-data="statsTableData"
          :height-offset="50"
          ref="dnsRequestByProgramStatsTable"
        ></tree-table-element>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import IQCollection from '@/resources/collections/IQCollection'
import BarChartJs from '@/components/chart/BarChartJs.vue'
import TopTenCard from '@/components/card/TopTenCard.vue'
import TreeTableElement from '@/components/table/TreeTableElement.vue'

import {
  HRLY_DNS_REQ_STATISTICS_LIST_REQ,
  TOP_DNS_REQ_STATISTICS_LIST_REQ,
  BOTTOM_DNS_REQ_STATISTICS_LIST_REQ,
  NEW_DNS_REQ_STATISTICS_LIST_REQ,
  DNS_REQ_HOST_STATISTICS_LIST_REQ
} from '@/store/modules/statistics/dns-statistics.type'

import {
  HRLY_EXTERNAL_IP_STATISTICS_LIST_REQ,
  TOP_EXTERNAL_IP_STATISTICS_LIST_REQ,
  BOTTOM_EXTERNAL_IP_STATISTICS_LIST_REQ,
  NEW_EXTERNAL_IP_STATISTICS_LIST_REQ
} from '@/store/modules/statistics/external-ip-statistics.type'

import {
  HRLY_LOGIN_FAILURES_STATISTICS_LIST_REQ,
  TOP_LOGIN_FAILURES_STATISTICS_LIST_REQ,
  BOTTOM_LOGIN_FAILURES_STATISTICS_LIST_REQ,
  NEW_LOGIN_FAILURES_STATISTICS_LIST_REQ
} from '@/store/modules/statistics/login-failures-statistics.type'

import {
  HRLY_PRIVILEGED_EXEC_STATISTICS_LIST_REQ,
  TOP_PRIVILEGED_EXEC_STATISTICS_LIST_REQ,
  BOTTOM_PRIVILEGED_EXEC_STATISTICS_LIST_REQ,
  NEW_PRIVILEGED_EXEC_STATISTICS_LIST_REQ
} from '@/store/modules/statistics/privileged-execution-statistics.type'

import {
  HRLY_INSIGHT_STATISTICS_LIST_REQ,
  TOP_INSIGHT_STATISTICS_LIST_REQ,
  BOTTOM_INSIGHT_STATISTICS_LIST_REQ,
  NEW_INSIGHT_STATISTICS_LIST_REQ
} from '@/store/modules/statistics/insight-statistics.type'

import { dnsRequestProgramListMetaInfo } from '@/column-header/statistics/dnsrequest-program-meta'
import { dnsRequestTopProgramListMetaInfo } from '@/column-header/statistics/dnsrequest-topprogram-meta'

const types = {
  'dns-requests': {
    id: 'dns-stats',
    actions: [
      HRLY_DNS_REQ_STATISTICS_LIST_REQ,
      TOP_DNS_REQ_STATISTICS_LIST_REQ,
      BOTTOM_DNS_REQ_STATISTICS_LIST_REQ,
      NEW_DNS_REQ_STATISTICS_LIST_REQ,
      DNS_REQ_HOST_STATISTICS_LIST_REQ
    ],
    fields: ['topLevelDomain', 'count'],
    colHeaders: ['Domain', 'Count'],
    apiKeyParam: 'domainNames',
    columns: dnsRequestTopProgramListMetaInfo.meta.columns
  },
  'external-ips': {
    id: 'ips-stats',
    actions: [
      HRLY_EXTERNAL_IP_STATISTICS_LIST_REQ,
      TOP_EXTERNAL_IP_STATISTICS_LIST_REQ,
      BOTTOM_EXTERNAL_IP_STATISTICS_LIST_REQ,
      NEW_EXTERNAL_IP_STATISTICS_LIST_REQ
    ],
    fields: ['sourceComm', 'count'],
    colHeaders: ['Program', 'Count']
  },
  'login-failures': {
    id: 'login-stats',
    actions: [
      HRLY_LOGIN_FAILURES_STATISTICS_LIST_REQ,
      TOP_LOGIN_FAILURES_STATISTICS_LIST_REQ,
      BOTTOM_LOGIN_FAILURES_STATISTICS_LIST_REQ,
      NEW_LOGIN_FAILURES_STATISTICS_LIST_REQ
    ],
    fields: ['sourceUser', 'count'],
    colHeaders: ['User Name', 'Count']
  },
  'privileged-execs': {
    id: 'privileged-execs-stats',
    actions: [
      HRLY_PRIVILEGED_EXEC_STATISTICS_LIST_REQ,
      TOP_PRIVILEGED_EXEC_STATISTICS_LIST_REQ,
      BOTTOM_PRIVILEGED_EXEC_STATISTICS_LIST_REQ,
      NEW_PRIVILEGED_EXEC_STATISTICS_LIST_REQ
    ],
    fields: ['sourceArgv', 'count'],
    colHeaders: ['Program', 'Count']
  },
  'process-launching-shells': {
    id: 'process-launching-shells-stats',
    actions: [
      HRLY_INSIGHT_STATISTICS_LIST_REQ,
      TOP_INSIGHT_STATISTICS_LIST_REQ,
      BOTTOM_INSIGHT_STATISTICS_LIST_REQ,
      NEW_INSIGHT_STATISTICS_LIST_REQ
    ],
    urlpath: 'process-launching-shells',
    fields: ['entity', 'count'],
    colHeaders: ['Program', 'Count']
  },
  'sensitive-file-access': {
    id: 'sensitive-file-access-stats',
    actions: [
      HRLY_INSIGHT_STATISTICS_LIST_REQ,
      TOP_INSIGHT_STATISTICS_LIST_REQ,
      BOTTOM_INSIGHT_STATISTICS_LIST_REQ,
      NEW_INSIGHT_STATISTICS_LIST_REQ
    ],
    urlpath: 'sensitive-file-access',
    fields: ['entity', 'count'],
    colHeaders: ['Program', 'Count']
  }
}

export default {
  name: 'Stats',
  props: {
    name: {
      type: String,
      default: () => {
        return ''
      }
    },
    agentId: {
      type: String,
      default: () => {
        return ''
      }
    },
    osType: {
      type: String,
      default: () => {
        return 'windows'
      }
    },
    selDate: {
      type: String,
      default: () => {
        return 'PAST_24_HOURS'
      }
    },
    type: {
      type: String,
      default: () => {
        return 'dns-requests'
      }
    },
    enableTableView: {
      type: Boolean,
      default: () => {
        return false
      }
    }
  },
  data () {
    return {
      loading: true,
      chartLoading: false,
      statsLoading: false,
      hasChartData: false,
      tableLoading: false,
      chartId: '',
      fields: '',
      hrlyStats: {},
      topStats: [],
      colHeaders: [],
      statsTableColumns: dnsRequestTopProgramListMetaInfo.meta.columns,
      statsTableData: {},
      selectedTopTenOption: 'top',
      topTenOptions: [
        { label: 'Top', value: 'top' },
        { label: 'Bottom', value: 'bottom' },
        { label: 'New', value: 'new' }
      ],
      startPageNo: 1
    }
  },
  created () {
    this.fetchHostInsights()
  },
  mounted () {
    // window resize event
    console.log('mounted')
  },
  methods: {
    fetchHostInsights () {
      let options = types[this.type]
      this.colHeaders = options.colHeaders
      this.loading = true
      this.$store
        .dispatch(options.actions[0], {
          queryDate: this.selDate,
          agentId: this.agentId,
          os: this.osType,
          path: options.urlpath
        })
        .then(
          stats => {
            this.hrlyStats.chartData = stats.chartData
            this.hrlyStats.chartOptions = stats.chartOptions

            this.loading = false
            if (this.hrlyStats.chartData.datasets[0].data.length > 0) {
              this.hasChartData = true
              this.changeTopTenSelection(this.topTenOptions[0])
              this.chartLoading = true
            } else {
              this.hasChartData = false
            }
            this.$emit('updated', { name: this.name, hasChartData: true })
          },
          errMsg => {
            console.log(errMsg.toString())
          }
        )
      this.$emit('updated', { name: this.name, hasChartData: false })
    },
    changeTopTenSelection (topTenSelect, event) {
      let options = types[this.type]
      let action = (val => {
        let action
        switch (val) {
          case 'top':
            action = options.actions[1]
            break
          case 'bottom':
            action = options.actions[2]
            break
          case 'new':
            action = options.actions[3]
            break
        }
        return action
      })(topTenSelect.value)
      if (this.$refs['toptenCard']) {
        this.$refs['toptenCard'].resetPageNum()
      }
      this.statsLoading = true
      this.selectedTopTenOption = topTenSelect.value
      this.$store
        .dispatch(action, {
          queryDate: this.selDate,
          agentId: this.agentId,
          os: this.osType,
          path: options.urlpath
        })
        .then(
          stats => {
            let resourceKeyParm = {}
            this.topStats = stats
            this.statsLoading = false
            if (this.enableTableView) {
              resourceKeyParm[options.apiKeyParam] = _.map(stats, options.fields[0])
              if (_.isEmpty(resourceKeyParm[options.apiKeyParam])) {
                resourceKeyParm = {}
              }
              this.statsTableColumns = options.columns
              this.getTableStats(options.actions[4], this.selectedTopTenOption, resourceKeyParm, false, false)
            }
          },
          errMsg => {
            console.log(errMsg.toString())
          }
        )
    },
    getTableStats (tableReq, tabSelection, resourceKeyParm, newCopy = true, showChildren = false) {
      this.tableLoading = true
      if (_.isEmpty(resourceKeyParm)) {
        if (!_.isEmpty(this.statsTableData)) {
          this.statsTableData.models.length = 0
          this.$nextTick(() => {
            this.tableLoading = false
          })
        } else {
          this.statsTableData = new IQCollection()
          this.tableLoading = false
        }
        return true
      }
      this.$store
        .dispatch(
          tableReq,
          _.merge(
            {
              queryDate: this.selDate,
              agentId: this.agentId,
              position: tabSelection,
              showChildren: showChildren
            },
            resourceKeyParm
          )
        )
        .then(stats => {
          if (!_.isEmpty(stats)) {
            stats.recordsPerPage = 5
          }
          if (newCopy) {
            this.tempTableStats = _.cloneDeep(stats)
          }
          this.statsTableData = stats
          this.tableLoading = false
        })
    },
    emitTopTenSelection (row) {
      this.$emit('stats-click', row)
    }
  },
  watch: {
    selDate: function () {
      this.loading = true
      this.fetchHostInsights()
    }
  },
  computed: {
    pageSize () {
      return 10
    },
    ...mapGetters(['hostInsightsDateRangeUserSetting'])
  },
  components: {
    BarChartJs,
    TopTenCard,
    TreeTableElement
  }
}
</script>

<style scoped>
@import 'assets/styles/_variables.css';

.el-tag--plain.el-tag--info {
  margin-right: 8px;
  text-transform: uppercase;
}

.el-tag--plain.el-tag--info.active {
  color: #1e80e8;
  font-weight: bold;
  background: #e4f4fe;
  border: solid 1px #187ce6;
}

>>> .top-ten {
  height: calc(100% - 75px) !important;
  margin-top: 10px;
}

>>> .subtitle2.row-bg.cursor-pointer.el-row.el-row--flex {
  padding: 0;
  color: #3c4c64;
  border: none;
  border-bottom: solid 1px #eaecf6;
}

.top-ten .top-ten_title {
  display: none;
}

.top-ten .row-bg .el-col-6 {
  text-align: right;
}

.topten-entities {
  height: 320px;
  overflow: hidden;
  border: 1px solid var(--border-color);
  padding: 8px;
}
</style>



// WEBPACK FOOTER //
// src/views/hosts/Stats.vue